// FormField<T>: a single form input's value, validation state, and touched
// flag, built on the same state<T> reactivity Component already uses — no
// new reactive primitive, and deliberately no DOM binding of its own
// (wiring Value to a real <input> is one addEventListener call in your own
// Render(), the same as Counter's own click handler — see Kopular's README).
//
// A validator is a plain (T) => string? function: null means valid, the
// same "null means nothing to report" convention KopScript's nullable
// types already use elsewhere. There's no array of validators — KopScript
// has no syntax for an array of function values — so combining more than
// one check is either an if-chain in one lambda, hand-written:
//
//   FormField<string> name = new FormField<string>("", (string v) => {
//     string? required = Validators.Required(v);
//     if (required != null) { return required; }
//     return Validators.MaxLength(v, 40);
//   });
//
// or the CombineValidators2/CombineValidators3 free functions below, for
// the common case of just chaining a couple of already-built validators
// with no custom logic of their own:
//
//   FormField<string> name = new FormField<string>("",
//     CombineValidators2((string v) => Validators.Required(v), (string v) => Validators.MaxLength(v, 40)));
//
// Free functions, not static Validators methods, and fixed-arity (2 and 3),
// not a general array-taking Validators.All(...): KopScript has no
// array-of-function-values type to accept a variable-length list with, and
// a generic function can only be a *free* function in v1 — a class's own
// static method can't introduce a new type parameter of its own beyond the
// class's (Validators itself isn't generic) — see KopScript's README/LLM.md
// "Generics" section for exactly that cut. Add CombineValidators4 etc. the
// same way if a real form ever needs to chain more than three.
(T) => string? CombineValidators2<T>((T) => string? v1, (T) => string? v2) {
  return (T value) => {
    string? r1 = v1(value);
    if (r1 != null) { return r1; }
    return v2(value);
  };
}

(T) => string? CombineValidators3<T>((T) => string? v1, (T) => string? v2, (T) => string? v3) {
  return (T value) => {
    string? r1 = v1(value);
    if (r1 != null) { return r1; }
    string? r2 = v2(value);
    if (r2 != null) { return r2; }
    return v3(value);
  };
}
class FormField<T> {
  public state<T> Value;
  public state<string?> Error;
  public state<bool> Touched;

  constructor(T initial, (T) => string? validate) {
    this.Value = state(initial);
    this.Error = state(validate(initial));
    this.Touched = state(false);
    this.Value.Subscribe((T v) => { this.Error.Value = validate(v); });
  }

  // Call on blur — separate from Error so a fresh, untouched field with an
  // invalid initial value (e.g. Required on an empty string) doesn't show
  // an error message before the user has had a chance to type anything.
  public void Touch() {
    this.Touched.Value = true;
  }

  public bool Valid() {
    return this.Error.Value == null;
  }
}

// A small set of common checks, each returning an error message or null —
// not a validation framework, just the handful of checks almost every form
// needs, so most fields don't have to hand-write string-length arithmetic.
class Validators {
  public static string? Required(string value) {
    if (value.Trim().Length == 0) { return "Required"; }
    return null;
  }

  public static string? MinLength(string value, number min) {
    if (value.Length < min) { return $"Must be at least {min} characters"; }
    return null;
  }

  public static string? MaxLength(string value, number max) {
    if (value.Length > max) { return $"Must be at most {max} characters"; }
    return null;
  }

  public static string? Email(string value) {
    return match value {
      r"^[^@\s]+@[^@\s]+\.[^@\s]+$" => null,
      _ => "Must be a valid email"
    };
  }

  public static string? Min(number value, number min) {
    if (value < min) { return $"Must be at least {min}"; }
    return null;
  }

  public static string? Max(number value, number max) {
    if (value > max) { return $"Must be at most {max}"; }
    return null;
  }
}
